
import Vue from "vue";
import { Alert, Vehicle } from "@/commons/types";
import Alerter from "@/components/Alerter.vue";
import { DeviceAlarmSchema, DeviceDataService } from "@/commons/client";
import { getAlarmInfo, AlarmInfo } from "@/constants/alerts";

export default Vue.extend({
  name: "AlarmGraph",
  components: { Alerter },

  data: () => {
    // Funzione helper per ottenere la data locale formattata
    const getLocalDateString = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      alerts: [] as Alert[],
      alarms: [] as DeviceAlarmSchema[],
      selectedVehicle: "all" as string | null,
      startDateOpen: false,
      endDateOpen: false,
      advancedFiltersOpen: null as number | null,
      search: "",
      filters: {
        alarm_type: null as string | null,
        device_id: null as string | null,
      },
      dateFilters: {
        from: getLocalDateString(),
        to: getLocalDateString(),
      },
    };
  },

  computed: {
    organizationId(): number {
      return this.$store.state.organizationId;
    },
    vehiclesList() {
      const list = this.$store.state.vehicles.map((v: Vehicle) => ({
        value: v.vehicle_id,
        label: `${v.plate} - ${v.vehicle_id}`,
      }));
      return [{ value: "all", label: "Tutti" }, ...list];
    },
    rules() {
      return this.$store.state.genericRules;
    },
    uniqueAlarmTypes(): string[] {
      const types = new Set(this.alarms.map((a) => a.alarm_type));
      return Array.from(types);
    },
    mappedAlarms(): AlarmInfo[] {
      return this.filteredAlarms.map((alarm) => ({
        ...alarm,
        ...getAlarmInfo(alarm.alarm_type),
      }));
    },
    filteredAlarms(): DeviceAlarmSchema[] {
      return this.alarms
        .filter((a) => {
          const matchType = this.filters.alarm_type
            ? a.alarm_type === this.filters.alarm_type
            : true;
          const matchDevice = this.filters.device_id
            ? a.device_id && a.device_id.includes(this.filters.device_id)
            : true;
          return matchType && matchDevice;
        })
        .filter((alarm) =>
          alarm.alarm_type.toLowerCase().includes(this.search.toLowerCase())
        );
    },
  },

  methods: {
    toggleFilters() {
      this.advancedFiltersOpen = this.advancedFiltersOpen === 0 ? null : 0;
    },
    localizeDtString(dtString: string): string {
      // Controlla se ha informazioni di timezone con regex più robusta
      const hasTimezone =
        dtString.includes("Z") ||
        dtString.includes("+") ||
        /T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}/.test(dtString);

      let date;
      if (hasTimezone) {
        date = new Date(dtString);
      } else {
        // Se non ha timezone, assumiamo sia UTC (tipico per timestamp server)
        const isoLike = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dtString);
        date = new Date(isoLike ? dtString + "Z" : dtString);
      }

      // Verifica se la data è valida
      if (isNaN(date.getTime())) {
        console.warn(`Data invalida: ${dtString}`);
        return dtString; // Ritorna il timestamp originale se non parsabile
      }

      return date.toLocaleString("it-IT", {
        timeZone: "Europe/Rome",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },

    resetFilters() {
      // Crea data locale senza conversione UTC
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const dateString = `${year}-${month}-${day}`;

      this.filters = {
        alarm_type: null,
        device_id: null,
      };
      this.dateFilters = {
        from: dateString,
        to: dateString,
      };
      this.getPoints();
    },
    async getPoints(): Promise<void> {
      try {
        const start = this.dateFilters.from + "T00:00:00.000Z";
        const stop = this.dateFilters.to + "T23:59:59.999Z";

        const vehicleId = this.selectedVehicle || "";
        const isAll = !vehicleId || vehicleId === "all";
        const result = isAll
          ? await DeviceDataService.organizationVehiclesDataAlarmsV1OrganizationsPkVehiclesVehicleIdDataAlarmsGet(
              this.organizationId,
              "all",
              start,
              stop
            )
          : await DeviceDataService.organizationVehiclesDataAlarmsV1OrganizationsPkVehiclesVehicleIdDataAlarmsGet(
              this.organizationId,
              vehicleId,
              start,
              stop
            );

        // Ordinamento migliorato con gestione errori
        this.alarms = result.sort((a, b) => {
          const dateA = new Date(a.timestamp || "");
          const dateB = new Date(b.timestamp || "");
          if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
          if (isNaN(dateA.getTime())) return 1;
          if (isNaN(dateB.getTime())) return -1;
          return dateB.getTime() - dateA.getTime(); // ordine DESC per vedere i più recenti sopra
        });
      } catch (err) {
        console.error("Errore nel recupero allarmi:", err);
        this.alerts.push({
          type: "error",
          text: "Errore durante il recupero degli allarmi",
        });
      }
    },
  },

  async mounted() {
    await this.getPoints();
  },
});
